import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Add, ContentCopy, Delete } from '@mui/icons-material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SavingsIcon from '@mui/icons-material/Savings';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import validLookup from 'utils/validLookup';
import useDialogModal from 'hooks/useDialogModal';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import { EmailTemplateService } from 'service/emailTemplateService';
import { EmailService } from 'service/emailService';
import { AttachmentService } from 'service/attachmentService';
import BulkMarketingEmailDialog from './BulkMarketingEmailDialog';
import 'react-quill/dist/quill.snow.css';

const FALLBACK_COMMUNICATION_TYPES = [
  { key: 'rx', label: 'RX' },
  { key: 'invoice', label: 'Invoice' },
  { key: 'appointment', label: 'Appointment' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'sale', label: 'Sale' }
];

const FALLBACK_PLACEHOLDERS = [
  { key: 'patient_name', label: 'Patient Name', isCommon: true },
  { key: 'appointment_date', label: 'Appointment Date', isCommon: true },
  { key: 'appointment_time', label: 'Appointment Time', isCommon: true },
  { key: 'invoice_number', label: 'Invoice Number', isCommon: true },
  { key: 'invoice_amount', label: 'Invoice Amount', isCommon: true },
  { key: 'clinic_name', label: 'Clinic Name', isCommon: true },
  { key: 'clinic_phone', label: 'Clinic Phone', isCommon: true }
];

const FALLBACK_PREVIEW_DATA = {
  patient_name: 'Jane Doe',
  appointment_date: 'July 15, 2026',
  appointment_time: '2:30 PM',
  invoice_number: 'INV-2026-001',
  invoice_amount: '$250.00',
  clinic_name: 'OptoDesk Optometry',
  clinic_phone: '(555) 123-4567',
  unsubscribe_link: 'https://example.com/preferences/unsubscribe?channel=email&preferenceType=email_marketing'
};

const FALLBACK_BODY_THEME_PRESETS = [
  {
    key: 'minimal',
    label: 'Minimal Clean',
    description: 'Simple clean layout with clear typography.',
    htmlTemplate: `
      <div data-email-theme-root="true" style="{{rootStyle}}">
        <h2 style="margin:0 0 12px 0">Hello {patient_name},</h2>
        <p style="margin:0 0 12px 0">Thank you for choosing {clinic_name}. We have a personalized update for you.</p>
        <p style="margin:0 0 12px 0">You can add your message here and customize fonts, text colors, highlights, and images.</p>
        <p style="margin:0 0 12px 0"><a href="https://example.com" style="{{ctaStyle}}">Take Action</a></p>
        <p style="margin:0">Call us anytime at {clinic_phone}.</p>
      </div>
    `
  },
  {
    key: 'hero-banner',
    label: 'Hero Banner',
    description: 'Large intro banner with call-to-action button.',
    htmlTemplate: `
      <div data-email-theme-root="true" style="{{rootStyle}}">
        <div style="padding:22px;border-radius:12px;background:linear-gradient(135deg,#e0f2fe 0%,#f8fafc 100%)">
          <p style="letter-spacing:1px;text-transform:uppercase;font-size:12px;margin:0 0 8px 0;opacity:0.8">{clinic_name}</p>
          <h2 style="margin:0 0 12px 0">A personalized update for {patient_name}</h2>
          <p style="margin:0 0 16px 0">Your next best pair is ready. Explore styles selected for your profile and save on your next order.</p>
          <a href="https://example.com" style="{{ctaStyle}}">Shop Recommended Frames</a>
        </div>
        <p style="margin-top:20px">Need help? Call us at {clinic_phone}.</p>
      </div>
    `
  },
  {
    key: 'card-grid',
    label: 'Feature Cards',
    description: 'Marketing layout with two feature blocks.',
    htmlTemplate: `
      <div data-email-theme-root="true" style="{{rootStyle}}">
        <h2 style="margin:0 0 12px 0">Fresh picks for {patient_name}</h2>
        <p style="margin:0 0 16px 0">Our team selected these options based on your preferences.</p>
        <table style="width:100%;border-collapse:separate;border-spacing:12px 0">
          <tr>
            <td style="background:#ffffff;padding:14px;border-radius:10px;vertical-align:top">
              <strong>Try at Home</strong>
              <p style="margin:8px 0 0 0">Pick up to 5 frames for a free at-home try-on.</p>
            </td>
            <td style="background:#ffffff;padding:14px;border-radius:10px;vertical-align:top">
              <strong>Member Discount</strong>
              <p style="margin:8px 0 0 0">Use code VISION15 for 15% off this week.</p>
            </td>
          </tr>
        </table>
        <p style="margin-top:20px"><a href="https://example.com" style="{{ctaStyle}}">Claim Offer</a></p>
      </div>
    `
  },
  {
    key: 'spotlight',
    label: 'Product Spotlight',
    description: 'Showcase one featured offer and supporting text.',
    htmlTemplate: `
      <div data-email-theme-root="true" style="{{rootStyle}}">
        <p style="margin:0 0 8px 0;opacity:0.85">Handpicked for you</p>
        <h2 style="margin:0 0 12px 0">Frame Spotlight: Percival</h2>
        <div style="background:#ffffff;padding:16px;border-radius:10px">
          <p style="margin:0 0 12px 0">A lightweight classic frame with modern fit and premium comfort.</p>
          <ul style="margin:0 0 12px 18px;padding:0">
            <li>Free lens fitting included</li>
            <li>30-day comfort guarantee</li>
            <li>In-store adjustments anytime</li>
          </ul>
          <a href="https://example.com" style="{{ctaStyle}}">View Frame</a>
        </div>
        <p style="margin-top:20px">Questions? Reply to this email and our staff will help.</p>
      </div>
    `
  }
];

const FALLBACK_COLOR_PALETTES = [
  { key: 'soft-neutral', label: 'Soft Neutral', textColor: '#1f2937', backgroundColor: '#f1f5f9' },
  { key: 'modern-blue', label: 'Modern Blue', textColor: '#0f172a', backgroundColor: '#dbeafe' },
  { key: 'champagne', label: 'Champagne', textColor: '#3f2f1f', backgroundColor: '#fef3c7' },
  { key: 'mint-fresh', label: 'Mint Fresh', textColor: '#064e3b', backgroundColor: '#d1fae5' },
  { key: 'charcoal-pop', label: 'Charcoal Pop', textColor: '#e5e7eb', backgroundColor: '#1f2937' }
];


const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean']
  ]
};


const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'link',
  'image'
];

const stripHtmlToText = (html) => {
  if (!html) {
    return '';
  }

  if (typeof window === 'undefined') {
    return String(html)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '')
    .replace(/\u00a0/g, ' ')
    .trim();
};

const convertPlainTextToHtml = (text) => {
  if (!text) {
    return '';
  }

  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  return String(text)
    .split('\n')
    .map((line) => `<p>${escapeHtml(line) || '<br />'}</p>`)
    .join('');
};

const buildThemeRootStyles = ({ textColor, backgroundColor }) => {
  const safeTextColor = textColor || '#1f2937';
  const safeBackgroundColor = backgroundColor || '#f1f5f9';
  return [
    `color: ${safeTextColor}`,
    `background-color: ${safeBackgroundColor}`,
    'padding: 24px',
    'border-radius: 12px',
    'font-family: Georgia, Cambria, Times New Roman, serif',
    'line-height: 1.6'
  ].join('; ');
};

const CTA_STYLE = 'display:inline-block;padding:10px 18px;border-radius:999px;background:#0ea5e9;color:#ffffff;text-decoration:none;font-weight:700';

const buildPresetHtmlFromTemplate = (htmlTemplate, textColor, backgroundColor) => {
  if (!htmlTemplate) {
    return '';
  }

  const rootStyle = buildThemeRootStyles({ textColor, backgroundColor });
  return String(htmlTemplate)
    .replace(/\{\{rootStyle\}\}/g, rootStyle)
    .replace(/\{\{ctaStyle\}\}/g, CTA_STYLE)
    .trim();
};

const normalizeColorToHex = (value) => {
  if (!value) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  const shortHexMatch = normalized.match(/^#([0-9a-f]{3})$/i);
  if (shortHexMatch) {
    return `#${shortHexMatch[1].split('').map((c) => `${c}${c}`).join('')}`;
  }

  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    return normalized;
  }

  const rgbMatch = normalized.match(/^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\)$/i);
  if (!rgbMatch) {
    return null;
  }

  const rgb = rgbMatch.slice(1, 4).map((channel) => Math.max(0, Math.min(255, Number(channel) || 0)));
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const extractThemeColorsFromHtml = (html) => {
  if (typeof window === 'undefined' || !html) {
    return null;
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  const root = container.querySelector('[data-email-theme-root="true"]');

  if (!root) {
    return null;
  }

  const textColor = normalizeColorToHex(root.style.color);
  const backgroundColor = normalizeColorToHex(root.style.backgroundColor);
  return {
    textColor,
    backgroundColor
  };
};

const upsertThemeRootWrapper = (html, textColor, backgroundColor) => {
  if (typeof window === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html || '';

  let root = container.querySelector('[data-email-theme-root="true"]');
  if (!root) {
    root = document.createElement('div');
    root.setAttribute('data-email-theme-root', 'true');
    while (container.firstChild) {
      root.appendChild(container.firstChild);
    }
    container.appendChild(root);
  }

  root.setAttribute('style', buildThemeRootStyles({ textColor, backgroundColor }));
  return container.innerHTML;
};

const renderPresetThumbnail = (preset, textColor, backgroundColor, previewData) => {
  const templateForThumbnail = preset?.thumbnailTemplate || preset?.htmlTemplate;
  const themedThumbnail = buildPresetHtmlFromTemplate(templateForThumbnail, textColor, backgroundColor);
  const hydratedThumbnail = String(themedThumbnail || '').replace(/\{([a-z0-9_]+)\}/gi, (_match, key) => {
    return previewData?.[key] || `{${key}}`;
  });

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        height: 104,
        backgroundColor: 'background.paper'
      }}
    >
      <Box
        sx={{
          width: '240%',
          transform: 'scale(0.42)',
          transformOrigin: 'top left',
          pointerEvents: 'none',
          '& *': {
            boxSizing: 'border-box'
          }
        }}
        dangerouslySetInnerHTML={{ __html: hydratedThumbnail }}
      />
    </Box>
  );
};

function Email() {
  const [
    ConfirmationDialogComp,
    showConfirmationDialog,
    closeConfirmationDialog,
  ] = useDialogModal(ConfirmationDialog);

  const [walletBalance, setWalletBalance] = useState(0);
  const [creditsToAdd, setCreditsToAdd] = useState('100');
  const [isLoading, setIsLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [communicationTypes, setCommunicationTypes] = useState(FALLBACK_COMMUNICATION_TYPES);
  const [placeholders, setPlaceholders] = useState(FALLBACK_PLACEHOLDERS);
  const [bodyThemePresets, setBodyThemePresets] = useState(FALLBACK_BODY_THEME_PRESETS);
  const [colorPalettes, setColorPalettes] = useState(FALLBACK_COLOR_PALETTES);
  const [previewData, setPreviewData] = useState(FALLBACK_PREVIEW_DATA);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState({
    id: null,
    templateName: '',
    communicationType: 'invoice',
    subject: '',
    body: '',
    footer: '',
    htmlBody: '',
    isDefault: false
  });

  const subjectTextFieldRef = useRef(null);
  const footerTextFieldRef = useRef(null);
  const quillRef = useRef(null);
  const imageUploadInputRef = useRef(null);
  const [focusedField, setFocusedField] = useState('body');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [marketingDialogOpen, setMarketingDialogOpen] = useState(false);
  const [selectedThemePreset, setSelectedThemePreset] = useState(FALLBACK_BODY_THEME_PRESETS[0].key);
  const [bodyTextColor, setBodyTextColor] = useState('#1f2937');
  const [bodyBackgroundColor, setBodyBackgroundColor] = useState('#f1f5f9');
  const [previewMode, setPreviewMode] = useState('current');

  const isMarketingTemplate = ['promotions', 'sale'].includes(String(currentTemplate.communicationType || '').toLowerCase());

  const syncThemeColorsFromHtml = (htmlBody) => {
    const colors = extractThemeColorsFromHtml(htmlBody);
    if (colors?.textColor) {
      setBodyTextColor(colors.textColor);
    }
    if (colors?.backgroundColor) {
      setBodyBackgroundColor(colors.backgroundColor);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          templatesResponse,
          metadataResponse,
          walletResponse
        ] = await Promise.all([
          EmailTemplateService.getTemplates(),
          EmailTemplateService.getMetadata(),
          EmailService.getWallet()
        ]);

        const fetchedTemplates = templatesResponse?.data || [];
        const metadata = metadataResponse?.data || {};
        const fetchedCommunicationTypes = metadata.communicationTypes || [];
        const fetchedPlaceholders = metadata.placeholders || [];
        const editorConfig = metadata.editorConfig || {};
        const fetchedPresets = Array.isArray(editorConfig.starterTemplates) && editorConfig.starterTemplates.length > 0
          ? editorConfig.starterTemplates
          : FALLBACK_BODY_THEME_PRESETS;
        const fetchedPalettes = Array.isArray(editorConfig.colorPalettes) && editorConfig.colorPalettes.length > 0
          ? editorConfig.colorPalettes
          : FALLBACK_COLOR_PALETTES;
        const fallbackTheme = FALLBACK_COLOR_PALETTES[0] || { textColor: '#1f2937', backgroundColor: '#f1f5f9' };
        const defaultTheme = editorConfig.defaultTheme || {
          textColor: fallbackTheme.textColor,
          backgroundColor: fallbackTheme.backgroundColor
        };
        const fetchedPreviewData = editorConfig.previewSamples || FALLBACK_PREVIEW_DATA;

        setTemplates(fetchedTemplates);
        setCommunicationTypes(fetchedCommunicationTypes.length > 0 ? fetchedCommunicationTypes : FALLBACK_COMMUNICATION_TYPES);
        setPlaceholders(fetchedPlaceholders.length > 0 ? fetchedPlaceholders : FALLBACK_PLACEHOLDERS);
        setBodyThemePresets(fetchedPresets);
        setColorPalettes(fetchedPalettes);
        setPreviewData(fetchedPreviewData);

        if (fetchedPresets.length > 0) {
          setSelectedThemePreset((prev) => {
            if (fetchedPresets.some((preset) => preset.key === prev)) {
              return prev;
            }
            return fetchedPresets[0].key;
          });
        }

        if (defaultTheme.textColor) {
          setBodyTextColor(defaultTheme.textColor);
        }

        if (defaultTheme.backgroundColor) {
          setBodyBackgroundColor(defaultTheme.backgroundColor);
        }

        if (fetchedTemplates.length > 0) {
          const firstTemplateHtml = fetchedTemplates[0].htmlBody || convertPlainTextToHtml(fetchedTemplates[0].body || '');
          setSelectedTemplateId(String(fetchedTemplates[0].id));
          setCurrentTemplate({
            ...fetchedTemplates[0],
            htmlBody: firstTemplateHtml,
            communicationType: fetchedTemplates[0].communicationType || 'invoice'
          });
          syncThemeColorsFromHtml(firstTemplateHtml);
        }

        setWalletBalance(walletResponse?.data?.balanceCredits || 0);
      } catch (error) {
        toast.error(`Failed to load email data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleTemplateFieldChange = (field, value) => {
    setCurrentTemplate((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTemplateSelect = (event) => {
    const id = event.target.value;
    setSelectedTemplateId(id);

    if (id === 'new') {
      handleNewTemplateClick();
      return;
    }

    const template = templates.find((t) => String(t.id) === String(id));
    const templateHtml = template?.htmlBody || convertPlainTextToHtml(template?.body || '');
    setCurrentTemplate(template || {
      id: null,
      templateName: '',
      communicationType: 'invoice',
      subject: '',
      body: '',
      footer: '',
      htmlBody: '',
      isDefault: false
    });
    if (template) {
      setCurrentTemplate({
        ...template,
        htmlBody: templateHtml,
        communicationType: template.communicationType || 'invoice'
      });
      syncThemeColorsFromHtml(templateHtml);
    }
    setIsNewTemplate(false);
    setPreviewMode('current');
  };

  const handleNewTemplateClick = () => {
    const fallbackTheme = colorPalettes[0] || { textColor: '#1f2937', backgroundColor: '#f1f5f9' };
    setSelectedTemplateId('new');
    setCurrentTemplate({
      id: null,
      templateName: '',
      communicationType: 'invoice',
      subject: '',
      body: '',
      footer: '',
      htmlBody: '',
      isDefault: false
    });
    setIsNewTemplate(true);
    setBodyTextColor(fallbackTheme.textColor || '#1f2937');
    setBodyBackgroundColor(fallbackTheme.backgroundColor || '#f1f5f9');
    setPreviewMode('current');
  };

  const getPresetHtml = (presetKey, textColor, backgroundColor) => {
    const selectedPreset = bodyThemePresets.find((preset) => preset.key === presetKey)
      || bodyThemePresets[0]
      || FALLBACK_BODY_THEME_PRESETS[0];

    const fallbackPreset = FALLBACK_BODY_THEME_PRESETS.find((preset) => preset.key === selectedPreset?.key)
      || FALLBACK_BODY_THEME_PRESETS[0];
    const htmlTemplate = selectedPreset?.htmlTemplate || fallbackPreset?.htmlTemplate;

    return buildPresetHtmlFromTemplate(htmlTemplate, textColor, backgroundColor);
  };

  const handleCopyDefaultTemplate = async () => {
    if (!currentTemplate?.id || !currentTemplate?.isDefault) {
      return;
    }

    try {
      const response = await EmailTemplateService.copyDefaultTemplate(currentTemplate.id);
      const copied = response?.data;
      if (!copied) {
        throw new Error('Invalid response from server');
      }

      const refreshed = await EmailTemplateService.getTemplates();
      const refreshedTemplates = refreshed?.data || [];
      setTemplates(refreshedTemplates);
      setCurrentTemplate(copied);
      setSelectedTemplateId(String(copied.id));
      setIsNewTemplate(false);
      toast.success('Default template copied. You can now customize it.');
    } catch (error) {
      toast.error(`Failed to copy default template: ${error.message}`);
    }
  };

  const handleDeleteTemplate = () => {
    if (!currentTemplate?.id || isNewTemplate || currentTemplate?.isDefault) {
      toast.error('Only custom templates can be deleted.');
      return;
    }

    showConfirmationDialog(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        try {
          await EmailTemplateService.deleteTemplate(currentTemplate.id);
          const filtered = templates.filter((t) => t.id !== currentTemplate.id);
          setTemplates(filtered);

          if (filtered.length > 0) {
            setCurrentTemplate(filtered[0]);
            setSelectedTemplateId(String(filtered[0].id));
          } else {
            handleNewTemplateClick();
          }
          setIsNewTemplate(false);
          toast.success('Template deleted successfully');
        } catch (error) {
          toast.error(`Failed to delete template: ${error.message}`);
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Delete Template',
      message: `Are you sure you want to delete "${currentTemplate.templateName}"?`
    });
  };

  const handleSaveTemplate = async () => {
    const currentHtmlBody = currentTemplate.htmlBody || convertPlainTextToHtml(currentTemplate.body || '');
    const hasThemeRoot = currentHtmlBody.includes('data-email-theme-root="true"');
    const shouldPersistThemeRoot = hasThemeRoot || isMarketingTemplate || previewMode === 'starter';
    const htmlBodyToSave = shouldPersistThemeRoot
      ? upsertThemeRootWrapper(currentHtmlBody, bodyTextColor, bodyBackgroundColor)
      : currentHtmlBody;
    const normalizedBody = (currentTemplate.body || stripHtmlToText(htmlBodyToSave)).trim();

    if (!currentTemplate.templateName || !currentTemplate.subject || !normalizedBody || !currentTemplate.communicationType) {
      toast.error('Template name, communication type, subject and body are required.');
      return;
    }

    try {
      const payload = {
        templateName: currentTemplate.templateName,
        communicationType: currentTemplate.communicationType,
        subject: currentTemplate.subject,
        body: normalizedBody,
        footer: currentTemplate.footer,
        htmlBody: htmlBodyToSave
      };

      let response;
      if (isNewTemplate) {
        response = await EmailTemplateService.saveTemplate(payload);
      } else {
        response = await EmailTemplateService.updateTemplate(currentTemplate.id, payload);
      }

      const saved = response?.data;
      if (!saved) {
        throw new Error('Invalid response from server');
      }

      const refreshed = await EmailTemplateService.getTemplates();
      const refreshedTemplates = refreshed?.data || [];
      setTemplates(refreshedTemplates);
      setCurrentTemplate(saved);
      syncThemeColorsFromHtml(saved.htmlBody);
      setSelectedTemplateId(String(saved.id));
      setIsNewTemplate(false);
      setPreviewMode('current');
      toast.success('Template saved successfully');
    } catch (error) {
      toast.error(`Failed to save template: ${error.message}`);
    }
  };

  /**
   * TODO: this should take user to a payment page and after successful payment, we need to record the payment info and top-up the wallet. We should also have a way to track credit usage per email sent for better transparency and troubleshooting.
   * @returns 
   */
  const handleTopUpWallet = async () => {
    const credits = parseInt(creditsToAdd, 10);
    if (!credits || credits <= 0) {
      toast.error('Please enter a valid positive credit amount.');
      return;
    }

    try {
      const response = await EmailService.topUpWallet(credits);
      setWalletBalance(response?.data?.balanceCredits || walletBalance + credits);
      toast.success('Credits added successfully');
    } catch (error) {
      toast.error(`Failed to add credits: ${error.message}`);
    }
  };

  const insertPlaceholder = (placeholderKey, targetRef) => {
    if (focusedField === 'body') {
      const editor = quillRef.current?.getEditor?.();
      if (!editor || currentTemplate.isDefault) {
        return;
      }

      const placeholderText = `{${placeholderKey}}`;
      const range = editor.getSelection(true);
      const index = range?.index ?? editor.getLength();
      editor.insertText(index, placeholderText, 'user');
      editor.setSelection(index + placeholderText.length, 0, 'user');
      return;
    }

    if (!targetRef || !targetRef.current) return;
    const input = targetRef.current.querySelector('textarea') || targetRef.current.querySelector('input');
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const placeholderText = `{${placeholderKey}}`;

    let currentFieldValue;
    let fieldToUpdate;
    if (targetRef === subjectTextFieldRef) {
      currentFieldValue = currentTemplate.subject || '';
      fieldToUpdate = 'subject';
    } else if (targetRef === footerTextFieldRef) {
      currentFieldValue = currentTemplate.footer || '';
      fieldToUpdate = 'footer';
    } else {
      return;
    }

    const newValue = currentFieldValue.substring(0, start) + placeholderText + currentFieldValue.substring(end);
    handleTemplateFieldChange(fieldToUpdate, newValue);

    setTimeout(() => {
      input.selectionStart = start + placeholderText.length;
      input.selectionEnd = start + placeholderText.length;
      input.focus();
    }, 0);
  };

  const handleBodyHtmlChange = (html, changeSource = 'user') => {
    const plainText = stripHtmlToText(html);
    setCurrentTemplate((prev) => ({
      ...prev,
      htmlBody: html,
      body: plainText
    }));
  };

  const applyBodyThemeColors = () => {
    if (currentTemplate.isDefault) {
      return;
    }

    const themedHtml = upsertThemeRootWrapper(currentTemplate.htmlBody || '', bodyTextColor, bodyBackgroundColor);
    handleBodyHtmlChange(themedHtml, 'system');
    setPreviewMode('current');
  };

  const handleApplyPreset = () => {
    if (currentTemplate.isDefault) {
      return;
    }

    const themedPresetHtml = getPresetHtml(selectedThemePreset, bodyTextColor, bodyBackgroundColor);
    handleBodyHtmlChange(themedPresetHtml, 'system');
    setPreviewMode('starter');
    toast.success('Template style applied. You can still edit it freely.');
  };

  const handleTextColorChange = (color) => {
    setBodyTextColor(color);
    setPreviewMode('starter');
  };

  const handleBackgroundColorChange = (color) => {
    setBodyBackgroundColor(color);
    setPreviewMode('starter');
  };

  const handlePaletteSelect = (palette) => {
    setBodyTextColor(palette.textColor);
    setBodyBackgroundColor(palette.backgroundColor);
    setPreviewMode('starter');
  };

  const getPlaceholderTargetRef = () => {
    if (focusedField === 'subject') {
      return subjectTextFieldRef;
    }

    if (focusedField === 'footer') {
      return footerTextFieldRef;
    }

    return null;
  };

  const handleImageUploadClick = () => {
    if (currentTemplate.isDefault) {
      return;
    }
    imageUploadInputRef.current?.click();
  };

  const handleImageSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WEBP, or GIF image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Please upload an image smaller than 5MB.');
      return;
    }

    try {
      setIsUploadingImage(true);
      const response = await AttachmentService.uploadEmailTemplateImage(file, () => {});
      const imageUrl = response?.data?.url;

      if (!imageUrl) {
        throw new Error('Upload did not return an image URL');
      }

      const editor = quillRef.current?.getEditor?.();
      if (!editor) {
        throw new Error('Editor is not ready');
      }

      const range = editor.getSelection(true);
      const index = range?.index ?? editor.getLength();
      editor.insertEmbed(index, 'image', imageUrl, 'user');
      editor.setSelection(index + 1, 0, 'user');
      toast.success('Image uploaded and inserted.');
    } catch (error) {
      toast.error(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const replacePreviewTokens = (text = '') => {
    let output = String(text || '');
    placeholders.forEach((ph) => {
      const replacement = previewData[ph.key] || `[${ph.label}]`;
      output = output.replace(new RegExp(`\\{${ph.key}\\}`, 'g'), replacement);
    });
    return output;
  };

  const renderPreviewHtml = () => {
    const editorHtml = currentTemplate.htmlBody || convertPlainTextToHtml(currentTemplate.body || '');
    const htmlBody = previewMode === 'starter'
      ? (editorHtml ? upsertThemeRootWrapper(editorHtml, bodyTextColor, bodyBackgroundColor) : getPresetHtml(selectedThemePreset, bodyTextColor, bodyBackgroundColor))
      : editorHtml;
    const footerHtml = currentTemplate.footer
      ? `<p>${replacePreviewTokens(currentTemplate.footer)}</p>`
      : '';
    return `${replacePreviewTokens(htmlBody)}${footerHtml}`;
  };

  if (isLoading) {
    return <Typography>Loading email configuration...</Typography>;
  }

  return (
    <>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Email Configuration
      </Typography>

      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="warning"
          startIcon={<CampaignIcon />}
          onClick={() => setMarketingDialogOpen(true)}
        >
          Send Marketing Campaign
        </Button>
      </Box>

      {/* commenting for now. This will be added in phase 2 when we add support for multiple providers and more complex configuration. For now, we will just use SES with global config. 
      <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Provider Setup</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Email Provider</InputLabel>
              <Select
                value={provider}
                label="Email Provider"
                onChange={(e) => setProvider(e.target.value)}
              >
                {Object.keys(EMAIL_PROVIDERS).map((key) => (
                  <MenuItem key={key} value={key}>{EMAIL_PROVIDERS[key].name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {provider && EMAIL_PROVIDERS[provider].fields.map((field) => (
            <Grid item xs={12} md={field.name === 'region' ? 6 : 12} key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                type={showSensitiveConfig ? 'text' : field.type}
                value={config[field.name] || ''}
                onChange={(e) => setConfig((prev) => ({ ...prev, [field.name]: e.target.value }))}
                InputProps={{
                  endAdornment: field.type === 'password' ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSensitiveConfig((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showSensitiveConfig ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Grid>
          ))}

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="From Email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="From Name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Reply-To Email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Estimated Cost Per Email ($)"
              value={estimatedCostPerEmail}
              onChange={(e) => setEstimatedCostPerEmail(Number(e.target.value || 0))}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleSaveConfig}>Save Configuration</Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Test Provider</Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Test Recipient Email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="outlined" onClick={handleSendTestEmail}>Send Test Email</Button>
          </Grid>
        </Grid>
      </Paper> */}

      <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto', mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Credits Wallet</Typography>
        <Alert severity={walletBalance < 50 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Current balance: <strong>{walletBalance}</strong> credits
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Credits to Add"
              value={creditsToAdd}
              onChange={(e) => setCreditsToAdd(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SavingsIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button variant="contained" onClick={handleTopUpWallet} fullWidth sx={{ height: '56px' }}>
              Add Credits
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto', mt: 3 }}>
        <Typography variant="h6">Email Templates</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Default templates are read-only. Copy a default template to create your own editable override.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Template</InputLabel>
            <Select value={selectedTemplateId} label="Select Template" onChange={handleTemplateSelect}>
              <MenuItem value=""><em>-- Select a Template --</em></MenuItem>
              {templates.map((template) => (
                <MenuItem key={template.id} value={String(template.id)}>
                  {template.templateName} {template.isDefault ? '(Default)' : '(Custom)'}
                </MenuItem>
              ))}
              <MenuItem value="new">
                <Add fontSize="small" sx={{ mr: 1 }} /> Create New Template
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {(selectedTemplateId || isNewTemplate) && (
          <Grid container spacing={2}>
            {currentTemplate?.isDefault && (
              <Grid item xs={12}>
                <Alert severity="info">
                  This is a default template. Use Copy Default to create an editable custom version for your tenant.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Template Name"
                value={currentTemplate.templateName || ''}
                onChange={(e) => handleTemplateFieldChange('templateName', e.target.value)}
                disabled={currentTemplate.isDefault}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Communication Type</InputLabel>
                <Select
                  value={currentTemplate.communicationType || ''}
                  label="Communication Type"
                  onChange={(e) => handleTemplateFieldChange('communicationType', e.target.value)}
                  disabled={currentTemplate.isDefault}
                >
                  {communicationTypes.map((type) => (
                    <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isMarketingTemplate && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Marketing templates must include the placeholder {'{unsubscribe_link}'} to stay compliant.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Subject"
                value={currentTemplate.subject || ''}
                onChange={(e) => handleTemplateFieldChange('subject', e.target.value)}
                disabled={currentTemplate.isDefault}
                required
                ref={subjectTextFieldRef}
                onFocus={() => setFocusedField('subject')}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Email Body</Typography>

              <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Starter Templates
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                      {bodyThemePresets.map((preset) => {
                        const isSelected = selectedThemePreset === preset.key;
                        return (
                          <Paper
                            key={preset.key}
                            variant="outlined"
                            onClick={() => {
                              if (!currentTemplate.isDefault) {
                                setSelectedThemePreset(preset.key);
                                setPreviewMode('starter');
                              }
                            }}
                            sx={{
                              p: 1,
                              cursor: currentTemplate.isDefault ? 'not-allowed' : 'pointer',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              boxShadow: isSelected ? 2 : 0,
                              opacity: currentTemplate.isDefault ? 0.65 : 1
                            }}
                          >
                            {renderPresetThumbnail(preset, bodyTextColor, bodyBackgroundColor, previewData)}
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />}
                              <Typography variant="subtitle2">{preset.label}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {preset.description}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Text Color"
                      type="color"
                      value={bodyTextColor}
                      onChange={(e) => handleTextColorChange(e.target.value)}
                      disabled={currentTemplate.isDefault}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Background"
                      type="color"
                      value={bodyBackgroundColor}
                      onChange={(e) => handleBackgroundColorChange(e.target.value)}
                      disabled={currentTemplate.isDefault}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        onClick={applyBodyThemeColors}
                        disabled={currentTemplate.isDefault}
                      >
                        Apply Colors
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApplyPreset}
                        disabled={currentTemplate.isDefault}
                      >
                        Apply Template
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Quick Color Palettes
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {colorPalettes.map((palette) => (
                        <Button
                          key={palette.key}
                          size="small"
                          variant="outlined"
                          onClick={() => handlePaletteSelect(palette)}
                          disabled={currentTemplate.isDefault}
                          sx={{ textTransform: 'none' }}
                          startIcon={(
                            <Box sx={{ display: 'flex', gap: 0.4 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: palette.backgroundColor, border: '1px solid #94a3b8' }} />
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: palette.textColor, border: '1px solid #94a3b8' }} />
                            </Box>
                          )}
                        >
                          {palette.label}
                        </Button>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: currentTemplate.isDefault ? 'action.disabledBackground' : 'background.paper',
                  '& .ql-toolbar': {
                    border: 'none',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider'
                  },
                  '& .ql-container': {
                    border: 'none',
                    minHeight: 220
                  },
                  '& .ql-editor': {
                    minHeight: 220
                  }
                }}
                onClick={() => setFocusedField('body')}
              >
                <ReactQuill
                  ref={quillRef}
                  value={currentTemplate.htmlBody || ''}
                  onChange={(html, _delta, source) => handleBodyHtmlChange(html, source)}
                  modules={quillModules}
                  formats={quillFormats}
                  readOnly={currentTemplate.isDefault}
                  theme="snow"
                />
              </Box>
              <FormHelperText>
                Rich formatting, links, images, and placeholders are supported.
              </FormHelperText>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Footer (Optional)"
                value={currentTemplate.footer || ''}
                onChange={(e) => handleTemplateFieldChange('footer', e.target.value)}
                disabled={currentTemplate.isDefault}
                ref={footerTextFieldRef}
                onFocus={() => setFocusedField('footer')}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {placeholders.filter((p) => p.isCommon).map((p) => (
                  <Chip
                    key={p.key}
                    label={p.label}
                    onClick={() => !currentTemplate.isDefault && insertPlaceholder(p.key, getPlaceholderTargetRef())}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
              <FormControl fullWidth>
                <InputLabel>Insert Placeholder</InputLabel>
                <Select
                  value=""
                  label="Insert Placeholder"
                  onChange={(e) => insertPlaceholder(e.target.value, getPlaceholderTargetRef())}
                  disabled={currentTemplate.isDefault}
                >
                  <MenuItem value=""><em>-- Select a Placeholder --</em></MenuItem>
                  {placeholders.map((p) => (
                    <MenuItem key={p.key} value={p.key}>{p.label} ({`{${p.key}}`})</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Placeholders are replaced with customer and invoice/appointment data at send time.
                </FormHelperText>
              </FormControl>
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <input
                  ref={imageUploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={handleImageSelected}
                />
                <Button
                  variant="outlined"
                  onClick={handleImageUploadClick}
                  disabled={currentTemplate.isDefault || isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading Image...' : 'Upload Image'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveTemplate}
                  disabled={currentTemplate.isDefault && !isNewTemplate}
                >
                  {isNewTemplate ? 'Create Template' : 'Save Template'}
                </Button>

                {currentTemplate?.isDefault && (
                  <Button variant="outlined" startIcon={<ContentCopy />} onClick={handleCopyDefaultTemplate}>
                    Copy Default
                  </Button>
                )}

                {!currentTemplate?.isDefault && currentTemplate?.id && (
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={handleDeleteTemplate}>
                    Delete
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {(selectedTemplateId || isNewTemplate) && (
        <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto', mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Email Preview</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={previewMode === 'starter' ? 'contained' : 'outlined'}
              onClick={() => setPreviewMode('starter')}
            >
              Styled Template
            </Button>
            <Button
              size="small"
              variant={previewMode === 'current' ? 'contained' : 'outlined'}
              onClick={() => setPreviewMode('current')}
            >
              Editor Content
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {previewMode === 'starter' ? 'Previewing styled editor content with current theme colors.' : 'Previewing current editor content without theme wrapper.'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fafafa',
            whiteSpace: 'pre-wrap'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AlternateEmailIcon fontSize="small" />
              To: {previewData.patient_name || FALLBACK_PREVIEW_DATA.patient_name}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Subject: {replacePreviewTokens(currentTemplate.subject || '')}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                '& img': { maxWidth: '100%', height: 'auto' },
                '& .ql-align-center': { textAlign: 'center' },
                '& .ql-align-right': { textAlign: 'right' },
                '& .ql-align-justify': { textAlign: 'justify' }
              }}
              dangerouslySetInnerHTML={{ __html: renderPreviewHtml() }}
            />
          </Box>
        </Paper>
      )}

      <ConfirmationDialogComp
        label={{
          positive: 'Delete',
          negative: 'Cancel'
        }}
      />

      <BulkMarketingEmailDialog
        open={marketingDialogOpen}
        onClose={() => setMarketingDialogOpen(false)}
      />
    </>
  );
}

export default Email;